import React, { createContext, useContext, useState, useCallback } from 'react';
import { querySalesforce, querySalesforceRecords, getServiceStatus, isThaiforceAvailable } from '../services/salesforce';

interface ServiceStatus {
  thaiforce: { available: boolean; error: string | null };
  salesforce: { available: boolean; error: string | null };
}

interface SalesforceQueryResponse<T> {
  records: T[];
  serviceUsed: 'THAIFORCE' | 'Salesforce';
  fallbackUsed: boolean;
  thaiforceError?: string;
  timestamp: string;
}

interface SalesforceContextType {
  query: <T>(soql: string) => Promise<T[]>;
  queryWithMetadata: <T>(soql: string) => Promise<SalesforceQueryResponse<T>>;
  isLoading: boolean;
  error: string | null;
  serviceStatus: ServiceStatus | null;
  checkServiceStatus: () => Promise<ServiceStatus>;
  isThaiforceAvailable: () => Promise<boolean>;
  lastServiceUsed: string | null;
}

const SalesforceContext = createContext<SalesforceContextType | undefined>(undefined);

export const SalesforceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serviceStatus, setServiceStatus] = useState<ServiceStatus | null>(null);
  const [lastServiceUsed, setLastServiceUsed] = useState<string | null>(null);

  const query = useCallback(async <T,>(soql: string): Promise<T[]> => {
    setIsLoading(true);
    setError(null);
    try {
      const results = await querySalesforceRecords<T>(soql);
      return results;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred while querying Salesforce';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const queryWithMetadata = useCallback(async <T,>(soql: string): Promise<SalesforceQueryResponse<T>> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await querySalesforce<T>(soql);
      setLastServiceUsed(response.serviceUsed);
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred while querying Salesforce';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const checkServiceStatus = useCallback(async (): Promise<ServiceStatus> => {
    try {
      const status = await getServiceStatus();
      setServiceStatus(status);
      return status;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to check service status';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const checkThaiforceAvailability = useCallback(async (): Promise<boolean> => {
    try {
      return await isThaiforceAvailable();
    } catch (err) {
      console.error('Failed to check THAIFORCE availability:', err);
      return false;
    }
  }, []);

  return (
    <SalesforceContext.Provider value={{
      query,
      queryWithMetadata,
      isLoading,
      error,
      serviceStatus,
      checkServiceStatus,
      isThaiforceAvailable: checkThaiforceAvailability,
      lastServiceUsed
    }}>
      {children}
    </SalesforceContext.Provider>
  );
};

export const useSalesforce = () => {
  const context = useContext(SalesforceContext);
  if (context === undefined) {
    throw new Error('useSalesforce must be used within a SalesforceProvider');
  }
  return context;
}; 